import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ItemForm, formWeightsToMg, splitGpDeposits, type ItemFormValues } from './item-form';
import { ItemPricingTab } from './item-pricing-tab';
import { useArchiveItem, useItem, useUpdateItem } from '@/hooks/use-items';
import { cn } from '@/lib/utils';
import { useT } from '@/i18n/use-t';
import { useLocaleStore } from '@/i18n/store';
import { mgToGramsText } from '@/lib/item-weight';

export function barcodeAttributeEntries(value: unknown): [string, string][] {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return [];
  return Object.entries(value)
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
    .sort(([left], [right]) => left.localeCompare(right));
}

export function ItemDetailPage() {
  const t = useT();
  const locale = useLocaleStore((s) => s.locale);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const itemQ = useItem(id);
  const update = useUpdateItem(id ?? '');
  const archive = useArchiveItem();
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'details' | 'pricing'>('details');

  if (itemQ.isLoading)
    return <p className="text-muted-foreground">{t('common.loading', 'Loading…')}</p>;
  if (itemQ.isError || itemQ.data === undefined)
    return <p className="text-destructive">{t('catalog.item.notFound', 'Item not found.')}</p>;

  const item = itemQ.data;
  // MICRO-43 P2b: GP's deposit link is read-only and travels beside the form.
  const { attributes: editableAttributes, gpDeposits } = splitGpDeposits(item.attributes);
  const defaults: ItemFormValues = {
    sku: item.sku,
    name: item.name,
    description: item.description ?? '',
    categoryId: item.categoryId,
    unitOfMeasure: item.unitOfMeasure,
    taxCategory: item.taxCategory,
    ageRestriction: item.ageRestriction,
    isVariableWeight: item.isVariableWeight,
    skipBaggingAllowed: item.skipBaggingAllowed,
    isMarked: item.isMarked,
    exciseStampCount: item.exciseStampCount,
    weightCheckMode: item.weightCheckMode,
    grossWeightGrams: mgToGramsText(item.grossWeightMg, locale),
    netWeightGrams: mgToGramsText(item.netWeightMg, locale),
    status: item.status,
    barcodes: item.barcodes.map((b) => ({ barcode: b.barcode, isPrimary: b.isPrimary })),
    attributes: editableAttributes,
  };
  const barcodeAttributes = Object.fromEntries(
    item.barcodes.map((barcode) => [barcode.barcode, barcodeAttributeEntries(barcode.attributes)]),
  );

  return (
    <div className="space-y-5">
      <header className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Link to="/catalog/items" className="hover:underline">
              {t('catalog.items.title', 'Catalog')}
            </Link>
            <span>/</span>
            <span>{item.category.name}</span>
          </div>
          <h1 className="text-2xl font-semibold">{item.name}</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-mono">{item.sku}</span>
            <Badge variant={item.status === 'active' ? 'success' : 'outline'}>{item.status}</Badge>
          </div>
        </div>
        <div className="flex gap-2">
          {item.status === 'active' ? (
            <Button
              variant="outline"
              onClick={() => {
                void archive.mutateAsync(item.id).then(() => navigate('/catalog/items'));
              }}
              disabled={archive.isPending}
            >
              {t('catalog.item.archive', 'Archive')}
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => {
                void update
                  .mutateAsync({ status: 'active' })
                  .then(() => navigate(`/catalog/items/${item.id}`));
              }}
              disabled={update.isPending}
            >
              {t('catalog.item.restore', 'Restore')}
            </Button>
          )}
        </div>
      </header>

      <div className="flex border-b">
        {(['details', 'pricing'] as const).map((tabId) => (
          <button
            key={tabId}
            type="button"
            onClick={() => setTab(tabId)}
            className={cn(
              'border-b-2 px-4 py-2 text-sm font-medium capitalize transition-colors',
              tab === tabId
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tabId === 'details'
              ? t('catalog.item.tabDetails', 'details')
              : t('catalog.item.tabPricing', 'pricing')}
          </button>
        ))}
      </div>

      {tab === 'details' && (
        <ItemForm
          defaultValues={defaults}
          weightSource={item.weightSource}
          barcodeAttributes={barcodeAttributes}
          gpDeposits={gpDeposits}
          submitLabel={t('catalog.common.saveChanges', 'Save changes')}
          busy={update.isPending}
          errorText={error}
          onSubmit={async (values) => {
            setError(null);
            try {
              const { grossWeightGrams: _gross, netWeightGrams: _net, ...rest } = values;
              await update.mutateAsync({
                ...rest,
                ...formWeightsToMg(values),
                description: values.description === '' ? undefined : values.description,
              });
            } catch (err) {
              setError(extractError(err));
            }
          }}
        />
      )}

      {tab === 'pricing' && <ItemPricingTab itemId={item.id} unitOfMeasure={item.unitOfMeasure} />}
    </div>
  );
}

function extractError(err: unknown): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string; issues?: { path: string; message: string }[] }
      | undefined;
    if (data?.issues !== undefined && data.issues.length > 0) {
      return data.issues.map((i) => `${i.path}: ${i.message}`).join('; ');
    }
    return data?.message ?? err.message;
  }
  return 'Unexpected error';
}
